import type { MarkushDrawingDocument, MarkushGraph, MarkushScaffold, MarkushFragment } from './markushDocument.ts';
import { connectedWithin, isPlaceholderPosition, isSingleRing } from './markushDocument.ts';

// #587 M2: the application-level invariants drawing-v11.schema.json documents but cannot enforce
// (cross-references between scaffold, variablePositions and rGroups). Returns every issue rather
// than throwing on the first, unlike validateDrawingDocument: a Markush drawing is authored in
// steps, so an editor needs to show "R2 has no definition yet" alongside a still-valid rest of
// the document.

export type MarkushIssueCode =
  | 'malformed-graph'
  | 'missing-coordinate'
  | 'missing-formal-charge'
  | 'orphan-placeholder'
  | 'duplicate-position'
  | 'position-not-placeholder'
  | 'r-group-bond-count'
  | 'attachment-point-bonded'
  | 'attachment-point-not-single-ring'
  | 'undefined-r-group'
  | 'empty-atom-list'
  | 'repeat-unit-invalid'
  | 'fragment-invalid';

export interface MarkushIssue {
  readonly code: MarkushIssueCode;
  readonly message: string;
  /** The scaffold node the issue is about, when there is one (for highlighting). */
  readonly atomId?: string;
  /** The R-group label the issue is about, when there is one. */
  readonly rGroupLabel?: string;
}

function graphIssues(graph: MarkushGraph, where: string): MarkushIssue[] {
  const issues: MarkushIssue[] = [];
  const nodeIds = new Set<string>();
  for (const node of graph.nodes) {
    if (!node.id || nodeIds.has(node.id)) {
      issues.push({ code: 'malformed-graph', message: `${where}: node ids must be unique and non-empty.` });
    }
    nodeIds.add(node.id);
  }
  const edgeIds = new Set<string>();
  const connections = new Set<string>();
  for (const edge of graph.edges) {
    const connection = [edge.source, edge.target].sort().join('\u0000');
    if (!edge.id || edgeIds.has(edge.id)) {
      issues.push({ code: 'malformed-graph', message: `${where}: edge ids must be unique and non-empty.` });
    } else if (!nodeIds.has(edge.source) || !nodeIds.has(edge.target)) {
      issues.push({ code: 'malformed-graph', message: `${where}: edge ${edge.id} references a missing node.` });
    } else if (edge.source === edge.target) {
      issues.push({ code: 'malformed-graph', message: `${where}: edge ${edge.id} is a self-loop.` });
    } else if (connections.has(connection)) {
      issues.push({ code: 'malformed-graph', message: `${where}: edge ${edge.id} duplicates another bond.` });
    }
    edgeIds.add(edge.id);
    connections.add(connection);
  }
  return issues;
}

/** Coordinates for every node, formal charges and bonds for every concrete atom/edge. */
function layerIssues(structure: MarkushScaffold, where: string): MarkushIssue[] {
  const issues: MarkushIssue[] = [];
  for (const node of structure.graph.nodes) {
    if (!structure.coordinateSet.coordinates[node.id]) {
      issues.push({
        code: 'missing-coordinate',
        message: `${where}: node ${node.id} has no coordinate.`,
        atomId: node.id
      });
    }
    if (structure.atoms[node.id] && structure.formalCharges[node.id] === undefined) {
      issues.push({
        code: 'missing-formal-charge',
        message: `${where}: atom ${node.id} has no formal charge.`,
        atomId: node.id
      });
    }
  }
  for (const edge of structure.graph.edges) {
    if (!structure.bonds[edge.id]) {
      issues.push({ code: 'malformed-graph', message: `${where}: edge ${edge.id} has no bond properties.` });
    }
  }
  return issues;
}

function fragmentIssues(fragment: MarkushFragment, label: string, index: number): MarkushIssue[] {
  const where = `${label} member ${index + 1}`;
  const issues = [...graphIssues(fragment.graph, where), ...layerIssues(fragment, where)];
  const fragmentIssue = (message: string): MarkushIssue => ({
    code: 'fragment-invalid',
    message: `${where}: ${message}`
  });
  if (fragment.graph.nodes.length === 0) issues.push(fragmentIssue('has no atoms.'));
  const placeholder = fragment.graph.nodes.find((node) => !fragment.atoms[node.id]);
  if (placeholder) {
    issues.push(fragmentIssue(`node ${placeholder.id} is not a concrete atom (fragments cannot nest positions).`));
  }
  if (!fragment.atoms[fragment.openValenceAtomId]) {
    issues.push(fragmentIssue(`open-valence atom ${fragment.openValenceAtomId} is not one of its atoms.`));
  }
  return issues.map((issue) => ({ ...issue, rGroupLabel: label }));
}

export function markushValidationIssues(document: MarkushDrawingDocument): readonly MarkushIssue[] {
  const { scaffold } = document;
  const issues: MarkushIssue[] = [...graphIssues(scaffold.graph, 'Scaffold'), ...layerIssues(scaffold, 'Scaffold')];
  const nodeIds = new Set(scaffold.graph.nodes.map((node) => node.id));
  const bondCount = new Map<string, number>();
  for (const edge of scaffold.graph.edges) {
    bondCount.set(edge.source, (bondCount.get(edge.source) ?? 0) + 1);
    bondCount.set(edge.target, (bondCount.get(edge.target) ?? 0) + 1);
  }

  const claimed = new Set<string>();
  for (const position of document.variablePositions.filter(isPlaceholderPosition)) {
    const { atomId } = position;
    if (!nodeIds.has(atomId) || scaffold.atoms[atomId]) {
      issues.push({
        code: 'position-not-placeholder',
        message: `${position.kind} position ${atomId} must name a scaffold node with no atom entry.`,
        atomId
      });
      continue;
    }
    if (claimed.has(atomId)) {
      issues.push({ code: 'duplicate-position', message: `Placeholder ${atomId} has more than one position.`, atomId });
    }
    claimed.add(atomId);
    const bonds = bondCount.get(atomId) ?? 0;
    if (position.kind === 'r-group' && bonds !== 1) {
      issues.push({
        code: 'r-group-bond-count',
        message: `R-group placeholder ${atomId} must have exactly one bond (has ${bonds}).`,
        atomId
      });
    }
    if (position.kind === 'attachment-point') {
      if (bonds !== 0) {
        issues.push({
          code: 'attachment-point-bonded',
          message: `Attachment point ${atomId} attaches through its ring, not a bond.`,
          atomId
        });
      }
      const concreteRing = position.ringAtomIds.every((id) => scaffold.atoms[id]);
      if (!concreteRing || !isSingleRing(scaffold.graph, position.ringAtomIds)) {
        issues.push({
          code: 'attachment-point-not-single-ring',
          message: `Attachment point ${atomId}'s ring atoms must form exactly one ring of concrete atoms.`,
          atomId
        });
      }
    }
    if (position.kind === 'atom-list' && new Set(position.allowedAtomicNumbers).size === 0) {
      issues.push({ code: 'empty-atom-list', message: `Atom list ${atomId} allows no elements.`, atomId });
    }
    if (
      (position.kind === 'r-group' || position.kind === 'attachment-point') &&
      !document.rGroups[position.rGroupLabel]
    ) {
      issues.push({
        code: 'undefined-r-group',
        message: `${position.rGroupLabel} is used but has no definition.`,
        atomId,
        rGroupLabel: position.rGroupLabel
      });
    }
  }

  for (const node of scaffold.graph.nodes) {
    if (!scaffold.atoms[node.id] && !claimed.has(node.id)) {
      issues.push({
        code: 'orphan-placeholder',
        message: `Scaffold node ${node.id} is neither an atom nor a variable position.`,
        atomId: node.id
      });
    }
  }

  const repeatedAtoms = new Set<string>();
  for (const position of document.variablePositions) {
    if (position.kind !== 'repeat-unit') continue;
    const firstAtomId = position.fragmentAtomIds[0];
    const invalid = (message: string) =>
      issues.push({
        code: 'repeat-unit-invalid',
        message: `Repeat unit: ${message}`,
        ...(firstAtomId ? { atomId: firstAtomId } : {})
      });
    const atoms = new Set(position.fragmentAtomIds);
    if (atoms.size === 0) invalid('spans no atoms.');
    if ([...atoms].some((id) => !scaffold.atoms[id])) invalid('every spanned node must be a concrete scaffold atom.');
    if ([...atoms].some((id) => repeatedAtoms.has(id))) invalid('overlaps another repeat unit (nesting is deferred).');
    atoms.forEach((id) => repeatedAtoms.add(id));
    const edgesById = new Map(scaffold.graph.edges.map((edge) => [edge.id, edge]));
    const spannedEdges = position.fragmentBondIds.map((id) => edgesById.get(id));
    if (spannedEdges.some((edge) => !edge || !atoms.has(edge.source) || !atoms.has(edge.target))) {
      invalid('every spanned bond must exist and join two spanned atoms.');
    }
    const adjacency = new Map<string, string[]>([...atoms].map((id) => [id, []]));
    for (const edge of spannedEdges) {
      if (edge && atoms.has(edge.source) && atoms.has(edge.target)) {
        adjacency.get(edge.source)!.push(edge.target);
        adjacency.get(edge.target)!.push(edge.source);
      }
    }
    if (!connectedWithin([...atoms], adjacency)) invalid('spanned atoms must be connected by spanned bonds.');
    if (!Number.isInteger(position.countMin) || position.countMin < 0) invalid('countMin must be a whole number ≥ 0.');
    if (
      position.countMax !== undefined &&
      (!Number.isInteger(position.countMax) || position.countMax < Math.max(1, position.countMin))
    ) {
      invalid('countMax must be a whole number ≥ countMin and ≥ 1.');
    }
  }

  for (const [label, definition] of Object.entries(document.rGroups)) {
    if (definition.kind !== 'enumerated') continue;
    if (definition.members.length === 0) {
      issues.push({ code: 'fragment-invalid', message: `${label} has no members.`, rGroupLabel: label });
    }
    definition.members.forEach((member, index) => issues.push(...fragmentIssues(member, label, index)));
  }
  return issues;
}
